// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import styles from './OwnersSection.module.css'
import OwnerNoteCard from './OwnerNoteCard'
import {
  ASK_COPY,
  BAND_CTA,
  DRAFT_WAITING_CTA,
  GATED_ASK_COPY,
  OWNERS_ANCHOR,
  OWNERS_SECTION_ID,
  ownerAskVisible,
  SIGN_IN_NEEDED,
} from 'lib/owner-notes/band-copy'
import OwnNoteCard from './OwnNoteCard'
import SectionHeader from 'components/product/SectionHeader'
import { fetchOwnerNotes } from 'lib/dealhunt/client'
import { loadQuestionSet } from 'lib/owner-notes/questions'
import {
  OWNER_DEFAULT_QUESTIONS,
  type OwnerClaimResponse,
  type OwnerNotePublic,
  type OwnerNoteSort,
  type OwnerPublishResponse,
  type OwnerQuestionSetDef,
  type OwnerReportReason,
  type OwnersEnrichment,
} from 'lib/dealhunt/types'
import { ownerApi } from 'lib/owner-notes/api'
import {
  useOnScreenOnce,
  useOwnerSurfaceTrack,
  useOwnerTrack,
  useOwnerViewBeacon,
} from 'lib/owner-notes/analytics'
import {
  NO_OWN_NOTE,
  onOpenOwnerBand,
  publishOwnNote,
  sectionOwnNoteSignal,
} from 'lib/owner-notes/bus'
import type { OwnerBandLocation } from './OwnerNoteBand'
import { ensureDevice } from 'lib/owner-notes/device'
import { ownViewsFromMe, useOwnNote } from 'lib/owner-notes/own-note'
import { useOwnerSessionOptional } from 'lib/owner-notes/auth-bridge'
import { PUBLIC_VIEWS_NUDGE, wallHasPublicCounts } from 'lib/owner-notes/views'
import { OWNER_DISCLOSURE } from 'lib/owner-notes/hints'
import { turnstileConfigured } from 'lib/owner-notes/turnstile'
import { useWriterHandoff } from 'lib/community/use-writer-handoff'
import HandoffHint from 'components/owner-notes/HandoffHint'
import { useHelpfulMine } from 'components/community/useHelpfulMine'
import type { CommunityHelpfulAnswer } from 'lib/community/types'
import { publicConfig } from 'config'
import { communityThreadHref } from 'lib/community/writer-handoff'
import Linked from 'shared-components/service/Linked'
import { SignInGateContext, signInGateFor } from 'lib/owner-notes/signin-gate'
import { bandDraftKey, useBandDraftWaiting } from 'lib/owner-notes/draft-waiting'

interface OwnersSectionProps {
  nodeId: string
  productName: string
  /** `enrichment.owners` from the product-line payload — the server-rendered truth. */
  owners: OwnersEnrichment | null | undefined
  /** This product page (`dealHuntProductHref`) — the writer handoff's product, so the server
   *  HTML already carries the link when the flag is on. */
  productHref?: string | null
}

// Defined in band-copy beside OWNERS_ANCHOR; re-exported for templates/ProductPageTemplate.tsx.
export { OWNERS_SECTION_ID }

// The three-step form is never in the server HTML and most readers never open it: it
// loads on the first "Leave an owner review".
const OwnerNoteBand = dynamic(() => import('./OwnerNoteBand'), { ssr: false })

/**
 * The PDP Owners section (contract §6/§9, #37, #51, #52, #54, #68). SERVER-RENDERED from
 * `enrichment.owners` — the count from the first note, the first page of notes
 * (photo-verified first, then newest), the aggregate percentages only past the gate —
 * never behind a tab. What hydrates in afterwards is visitor-specific and never in the
 * edge-cached HTML: the author's own note (only when the device cookie exists), the
 * report controls, further pages, and the band.
 */
/**
 * The reader's ordering choices (#119). `best` and `critical` are listed as a PAIR and neither
 * ships without the other: #54 is a compliance row — the UK DMCC Act bans giving greater
 * prominence to positive reviews — and a wall that offers "best" alone is exactly that act.
 * `default` keeps its own button so a reader can see, and get back to, the documented order.
 */
const OWNER_SORTS: ReadonlyArray<{ id: OwnerNoteSort; label: string }> = [
  { id: 'default', label: 'Newest' },
  { id: 'best', label: 'Best' },
  { id: 'critical', label: 'Most critical' },
]

/** "sorted by newest" in the wall's footer. */
const SORT_WORD: Record<OwnerNoteSort, string> = {
  default: 'newest',
  latest: 'newest',
  best: 'best first',
  critical: 'most critical first',
}

const OwnersSection: React.VFC<OwnersSectionProps> = ({
  nodeId,
  productName,
  owners,
  productHref = null,
}) => {
  const product = useMemo(() => ({ nodeId, name: productName }), [nodeId, productName])
  const trackOwner = useOwnerTrack(product)
  // The band's product carries this page's address, so its accept result links the review's
  // community thread (and the owners anchor). A memo of its own: `trackOwner` keeps its identity.
  const bandProduct = useMemo(() => ({ ...product, href: productHref }), [product, productHref])

  // SSR renders labels from the default set; the category's live set replaces it on hydration.
  const [questions, setQuestions] = useState<OwnerQuestionSetDef>(OWNER_DEFAULT_QUESTIONS)
  const [notes, setNotes] = useState<OwnerNotePublic[]>(owners?.notes ?? [])
  const [count, setCount] = useState(owners?.published_count ?? 0)
  // The wall's own total, from the list responses only. `count` is the header's owner count and
  // moves with local publishes/claims; paging on it offered "Show more owner reviews (1)" for a note that
  // was never in the list (the author's own, shown above).
  const [listTotal, setListTotal] = useState(owners?.published_count ?? 0)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<OwnerNoteSort>('default')
  const [sorting, setSorting] = useState(false)
  // The use-case filter (design 2a): a use-case option id, or null for every note. The chips
  // come from the aggregate's top use cases with their counts; the filter is server-side —
  // the wall paginates, so filtering what is loaded would answer "owners who commute" with the
  // commuters of the first twelve. Not remembered anywhere (no URL, no storage), like the sort.
  const [useCase, setUseCase] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hydrated, setHydrated] = useState(false)
  // The author's own note: private, no-store, only when this browser has a device cookie.
  // `ownPending` is set in the same effect flush as `setHydrated(true)`, so the first hydrated
  // render already holds the ask back instead of showing it until `/me` answers.
  const {
    me,
    ownNote,
    claimed,
    pending: ownPending,
    applyResult,
    applyClaim,
    applyDeleted,
    clearDevice,
  } = useOwnNote(nodeId)
  const [bandOpen, setBandOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  // Which ask opened the band — what `band_opened` reports, so the rail's opens are the rail's.
  const [bandOrigin, setBandOrigin] = useState<OwnerBandLocation>('section')
  const bandAnchor = useRef<HTMLDivElement>(null)

  const aggregate = owners?.aggregate ?? null
  const useCaseChips = aggregate?.use_cases.filter(u => u.count > 0) ?? []
  const useCaseLabel = useCase ? useCaseChips.find(u => u.id === useCase)?.label ?? null : null
  const hasMore = listTotal > notes.length

  // The rail ask (inside ProductHero) asks us to open — scroll here and open the band.
  useEffect(
    () =>
      onOpenOwnerBand(origin => {
        setEditing(false)
        setBandOrigin(origin ?? 'rail')
        setBandOpen(true)
        bandAnchor.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }),
    []
  )

  useEffect(() => {
    setHydrated(true)
    // The shared load: the band opened from this section reuses it instead of refetching.
    let cancelled = false
    loadQuestionSet(nodeId)
      .then(qs => !cancelled && qs && setQuestions(qs))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per product
  }, [nodeId])

  // The hero rail ask reads this (`OwnerRailAsk` → `railAskMode`): "See your review" for an
  // author, nothing while `/me` is pending. Reset on unmount so the next product starts clean.
  useEffect(() => {
    publishOwnNote(
      sectionOwnNoteSignal({ pending: ownPending, hasNote: ownNote !== null, bandOpen })
    )
  }, [ownPending, ownNote, bandOpen])
  useEffect(() => () => publishOwnNote(NO_OWN_NOTE), [])

  // A report from a browser without a device mints one first — with the reporting card's
  // own bot check, so a challenge shows beside the note being reported.
  const onReport = async (
    noteId: string,
    reason: OwnerReportReason,
    botToken: () => Promise<string>
  ): Promise<void> => {
    await ensureDevice(botToken)
    await ownerApi.report(noteId, reason)
  }

  const loadMore = async (): Promise<void> => {
    if (loadingMore) {
      return
    }
    setLoadingMore(true)
    try {
      const next = await fetchOwnerNotes(nodeId, page + 1, sort, undefined, useCase)
      if (next) {
        setPage(next.page)
        setCount(next.published_count)
        setListTotal(next.total ?? next.published_count)
        setNotes(prev => {
          const seen = new Set(prev.map(n => n.id))
          return [...prev, ...next.notes.filter(n => !seen.has(n.id))]
        })
      }
    } finally {
      setLoadingMore(false)
    }
  }

  /**
   * A reader re-orders the wall (#119). The fetch goes back to page 1 and REPLACES the list:
   * the wall paginates at 12, so re-ordering what is already loaded would answer "most
   * critical" with the most critical of the first twelve — a wrong answer wearing a confident
   * label. The order is not remembered anywhere (no URL, no storage): the page's own order
   * stays #54's sentiment-blind one for every new visit, and for every crawler.
   */
  const refetch = async (nextSort: OwnerNoteSort, nextUseCase: string | null): Promise<void> => {
    if (sorting) {
      return
    }
    setSorting(true)
    try {
      const res = await fetchOwnerNotes(nodeId, 1, nextSort, undefined, nextUseCase)
      if (res) {
        // The echo is the server's answer, not our request: a DealHunt without #119 deployed
        // ignores `sort` and echoes nothing, and the list really is still the default order —
        // so the control snaps back rather than claiming an order the wall is not in.
        setSort(res.sort ?? 'default')
        setCount(res.published_count)
        setPage(res.page)
        // The same for the filter — with one bridge: a DealHunt that ignores `use_case` has
        // still sent EVERY note when the product has no more than a page of them, and a filter
        // over the complete list is the same answer the server would give. Past a page the
        // wall would be filtering the first twelve, so the chip stays unpressed instead.
        const echoed = res.use_case ?? null
        if (nextUseCase && echoed === null && res.published_count <= res.notes.length) {
          const local = res.notes.filter(n => n.answers.use_cases?.includes(nextUseCase))
          setUseCase(nextUseCase)
          setNotes(local)
          setListTotal(local.length)
          return
        }
        setUseCase(echoed)
        setNotes(res.notes)
        setListTotal(res.total ?? res.published_count)
      }
    } finally {
      setSorting(false)
    }
  }

  const onSort = async (next: OwnerNoteSort): Promise<void> => {
    if (next !== sort) {
      await refetch(next, useCase)
    }
  }

  const onFilter = async (next: string | null): Promise<void> => {
    if (next !== useCase) {
      await refetch(sort, next)
    }
  }

  const onResult = (result: OwnerPublishResponse): void => {
    // Optimistic: the author's card comes straight from the server answer (#51/§6).
    applyResult(result)
    if (result.verdict.outcome === 'accept') {
      setCount(result.owners_now)
    }
  }

  const onClaimed = (result: OwnerClaimResponse): void => {
    // Counted only when DealHunt names THIS page's held note (#120) — never the profile-wide count.
    if (applyClaim(result)) {
      setCount(c => c + 1)
    }
  }

  const openBand = (): void => {
    setEditing(false)
    setBandOrigin('section')
    setBandOpen(true)
  }
  // One Review Writer (P1): with the flag on for `section`, the box and the wall footer link the
  // community writer (back to this page's #owners) instead of opening the band here. An edit
  // from the author's own card still opens it in place.
  const handoff = useWriterHandoff('section', productHref, { hash: OWNERS_ANCHOR })
  // The notice follows what the ask actually opens — the same `handoff` the control below uses
  // (null or not is build flag + product slug only, so the server and the client agree).
  const askGated = handoff ? signInGateFor('community') : signInGateFor('product')
  // A started draft in this tab (after mount only): the in-place ask says so; the band restores it.
  const draftWaiting = useBandDraftWaiting(nodeId, !handoff && askGated, bandOpen)
  const askControl = handoff ? (
    <Linked href={handoff.href()} className={styles.askBtn} {...handoff.linkProps}>
      {BAND_CTA}
    </Linked>
  ) : (
    <button type="button" className={styles.askBtn} onClick={openBand}>
      {draftWaiting ? DRAFT_WAITING_CTA : BAND_CTA}
    </button>
  )

  const showAsk = ownerAskVisible({
    configured: turnstileConfigured(),
    hydrated,
    hasOwnNote: ownNote !== null,
    bandOpen,
    ownPending,
  })
  // The section's impression: the ask block itself, on screen — this section is the bottom of a
  // long page, so a render-time mark (what it was until 2026-09-18) counted every page load.
  const askRef = useRef<HTMLDivElement>(null)
  const askSeen = useOnScreenOnce(askRef, showAsk)
  const askShown = useRef(false)
  useEffect(() => {
    if (showAsk && askSeen && !askShown.current) {
      askShown.current = true
      trackOwner('ask_shown', { location: 'section' })
    }
  }, [showAsk, askSeen, trackOwner])

  // The author's own card (above) and the public list both held their published note — the
  // list is server-rendered for everyone, the own card fetched for this browser. Once the own
  // card shows, drop its public twin (same note id); the count and "show more" stay as is.
  const listed = ownNote && !bandOpen ? notes.filter(note => note.id !== ownNote.id) : notes
  const ownViews = ownViewsFromMe(me, nodeId)
  // "Helpful · Reply →" on every public card (owner funnel C11): the public count is in the HTML;
  // the reader's own votes arrive in ONE batched read over the cards that carry a count (the
  // feed's `useHelpfulMine` — re-read when sort, filter or Show more change the list), and the
  // wall remembers DealHunt's answers so a card re-rendered by a sort keeps the vote.
  const helpfulMemory = useRef(new Map<string, CommunityHelpfulAnswer>()).current
  const helpfulMine = useHelpfulMine(
    listed.filter(note => typeof note.helpful_count === 'number').map(note => note.id)
  )
  const trailingSlash = publicConfig('useTrailingSlash')
  const onReply = (): void => trackOwner('notes_click', { location: 'reply', item_count: count })
  // Views (#138): a public card half on screen for a second counts once; the own note never.
  const notesRef = useRef<HTMLDivElement>(null)
  const surface = useOwnerSurfaceTrack()
  useOwnerViewBeacon(notesRef, {
    enabled: hydrated,
    skip: ownNote?.id ?? null,
    deps: [listed.length, sort],
  })
  // Every note's count, for a signed-in reader (#155). Client-only, after the session check:
  // the server pass and a signed-out reader get no figure, and the HTML never carries one.
  // A signed-out reader gets one line instead, and only when there is a count to see.
  const session = useOwnerSessionOptional()
  const showCounts = hydrated && session.resolved && session.signedIn
  const countsGated =
    hydrated && session.resolved && !session.signedIn && wallHasPublicCounts(listed)
  const countsShownFor = useRef<string | null>(null)
  useEffect(() => {
    // Once per page, on the first wall that carried a figure — not once per note.
    if (!showCounts || !wallHasPublicCounts(listed) || countsShownFor.current === nodeId) {
      return
    }
    countsShownFor.current = nodeId
    surface('views_shown', { location: 'wall', kind: 'count', target_product_id: nodeId })
    // `listed` is read when `showCounts` flips; `surface` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCounts, listed.length, nodeId])

  return (
    <section id={OWNERS_ANCHOR} className={styles.section} aria-labelledby="owners-heading">
      <div id="owners-heading">
        <SectionHeader
          title={count > 0 ? `Owners (${count})` : 'Owners'}
          intro={
            count > 0
              ? // The verb agrees with the subject: one reader reports, readers report. The
                // sentence is indexable text on every PDP with a note, so it is not a typo
                // one page wide (SEO audit 2026-09-18).
                `What ${
                  count === 1
                    ? `a reader who owns the ${productName} reports`
                    : `readers who own the ${productName} report`
                } after living with it. Owner-reported, checked before publishing; ordered photo-verified first, then newest.`
              : `Nobody who owns the ${productName} has written in yet.`
          }
          link={{ href: '/how-we-verify-owners/', label: 'How we verify owners', external: false }}
        />
      </div>
      {/* #68 wants a disclosure adjacent to the notes; the page-level affiliate line already
          sits above, so this is the owner-specific half of the verify page's statement. It
          stays ABOVE the notes (a footer would be buried under a long list) and reads as the
          intro's second line — not a block floating between the intro and the aggregate. */}
      <p className={styles.ethics}>{OWNER_DISCLOSURE}</p>

      <div ref={bandAnchor} className={styles.bandAnchor}>
        {bandOpen && (
          // The sign-in gate for the product page (always on, a constant): the band reads it. The
          // band is client-only (`ssr: false`), so nothing of the gate's reader-specific copy
          // reaches the server HTML.
          <SignInGateContext.Provider value={signInGateFor('product')}>
            <OwnerNoteBand
              product={bandProduct}
              existing={editing ? ownNote : null}
              location={bandOrigin}
              trackOwner={trackOwner}
              onClose={() => setBandOpen(false)}
              onResult={onResult}
              onClaimed={onClaimed}
              me={me}
              onDeviceForgotten={clearDevice}
              // Per tab, product id + surface: the rail and section asks open the same draft.
              draftKey={bandDraftKey(nodeId)}
            />
          </SignInGateContext.Provider>
        )}
        {!bandOpen && ownNote && (
          <OwnNoteCard
            note={ownNote}
            claimed={claimed}
            trackOwner={trackOwner}
            location="own_note"
            views={ownViews}
            questions={questions}
            onEdit={() => {
              setEditing(true)
              setBandOrigin('own_note')
              setBandOpen(true)
            }}
            onDeleted={() => {
              const gone = applyDeleted()
              if (!gone) {
                return
              }
              // One read of the list for both updates below.
              const twinListed = notes.some(note => note.id === gone.id)
              // Only a published note was ever counted; a held or rejected one was not.
              if (gone.status === 'published') {
                setCount(c => Math.max(0, c - 1))
              }
              // Its public twin was hidden while the own card showed — drop it, or the deleted
              // note reappears in the wall until reload.
              if (twinListed) {
                setNotes(prev => prev.filter(note => note.id !== gone.id))
                setListTotal(t => Math.max(0, t - 1))
              }
            }}
            onClaimed={onClaimed}
          />
        )}
        {/* Before the first note the ask is this box; from the first note it is the wall's
            footer button (design 2a) — never both (2026-09-22). */}
        {showAsk && count === 0 && (
          <div className={styles.ask} ref={askRef}>
            <div>
              <p className={styles.askTitle}>{ASK_COPY.title(productName)}</p>
              <p className={styles.askText}>
                {/* Reader-neutral and build-flag-only, so safe in the cached HTML: a handed-off
                    ask follows the community gate, which is on too. */}
                {askGated ? GATED_ASK_COPY.section : ASK_COPY.section}
              </p>
              {handoff && <HandoffHint text={handoff.hint} />}
            </div>
            {askControl}
          </div>
        )}
      </div>

      {count > 0 && (
        <div className={styles.wall}>
          <div className={styles.wallHead}>
            <p className={styles.wallTitle} id="owner-sort-label">
              {`${count} owner review${count === 1 ? '' : 's'}`}
            </p>
            {/* Buttons, never links: a sentiment-ordered URL is not something a crawler
                should be able to reach, and #51 wants the indexed order to be the
                server-rendered one. Best and Most critical are a PAIR (#54). */}
            {count > 1 && hydrated && (
              <div className={styles.sortRow}>
                <span className={styles.sortLabel}>{'Sort'}</span>
                <div className={styles.sortSeg} role="group" aria-labelledby="owner-sort-label">
                  {OWNER_SORTS.map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      className={styles.sortBtn}
                      aria-pressed={sort === opt.id}
                      disabled={sorting}
                      onClick={() => void onSort(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {hydrated && useCaseChips.length > 0 && (
            <div
              className={styles.chips}
              role="group"
              aria-label="Show reviews from owners who use it for"
            >
              <button
                type="button"
                className={styles.chip}
                aria-pressed={useCase === null}
                disabled={sorting}
                onClick={() => void onFilter(null)}
              >
                {'All reviews'}
                <span className={styles.chipCount}>{count}</span>
              </button>
              {useCaseChips.map(u => (
                <button
                  key={u.id}
                  type="button"
                  className={styles.chip}
                  aria-pressed={useCase === u.id}
                  disabled={sorting}
                  onClick={() => void onFilter(u.id)}
                >
                  {u.label}
                  <span className={styles.chipCount}>{u.count}</span>
                </button>
              ))}
            </div>
          )}
          {countsGated && (
            <p className={styles.viewsNudge}>
              {PUBLIC_VIEWS_NUDGE}{' '}
              <button
                type="button"
                className={styles.viewsNudgeBtn}
                onClick={() => void session.showLogIn()}
              >
                {'Sign in'}
              </button>
            </p>
          )}
          {listed.length > 0 ? (
            <div className={styles.notes} ref={notesRef}>
              {listed.map(note => (
                <OwnerNoteCard
                  key={note.id}
                  note={note}
                  questions={questions}
                  views={showCounts ? note.views_total ?? null : null}
                  onReport={hydrated ? onReport : undefined}
                  productName={productName}
                  engagement={{
                    thread: communityThreadHref(productHref, note.id, trailingSlash),
                    known: helpfulMine.get(note.id),
                    memory: helpfulMemory,
                    onReply,
                  }}
                />
              ))}
            </div>
          ) : (
            useCase && <p className={styles.fine}>{'No other owner picked this use case yet.'}</p>
          )}
          {hasMore && (
            <div className={styles.moreRow}>
              <button
                type="button"
                className={styles.moreBtn}
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                {loadingMore ? 'Loading…' : `Show more owner reviews (${listTotal - notes.length})`}
              </button>
            </div>
          )}
          <div className={styles.wallFoot}>
            <span className={styles.fine}>
              {[
                `Showing ${listed.length} of ${listTotal} owner review${
                  listTotal === 1 ? '' : 's'
                }`,
                useCaseLabel,
                `sorted by ${SORT_WORD[sort]}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            {showAsk && (
              <div ref={askRef}>
                {askControl}
                {handoff && <HandoffHint text={handoff.hint} />}
                {/* The footer's ask opens the gated band too: say so beside it, as the box does. */}
                {askGated && <p className={styles.fine}>{SIGN_IN_NEEDED}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

export default OwnersSection
